import { detalleLiquidableDelMes } from "./comisionDelMes";
import { crearHaberEmpleado } from "./pagosEmpleados";
import { client } from "./supabase";

// Materialización de la comisión LIQUIDABLE (cobro real, factoring ya excluido en comisionDelMes)
// como haberes `origen='motor_comision'`, por-venta e idempotente. El disparador (UI) va con la
// vista "próximo sueldo" — acá solo el mecanismo. Ver FASE 3.2b-2.

// Corte de transición: los cobros ANTERIORES a esta fecha quedan saldados por los recibos jun+jul
// (cargados aparte). Solo se materializan cobros con fecha_cobro_real >= COMISION_CORTE.
export const COMISION_CORTE = "2026-08-01";

// Referencias ya materializadas como comisión (marcador de idempotencia): Set de
// `${empleado_id}:${referencia}`. Incluye el baseline de junio (ventas 1922/1749) → esas ventas
// nunca se re-pagan aunque su cobro real caiga post-corte (caso WOM/1749, factoring adelantado).
const cargarReferenciasComisionDB = async () => {
  const { data, error } = await client
    .from("haberes_empleado")
    .select("empleado_id, referencia")
    .eq("tipo", "comision")
    .not("referencia", "is", null);
  if (error) throw error;
  return new Set((data || []).map((r) => `${r.empleado_id}:${r.referencia}`));
};

/**
 * Materializa las comisiones cobradas en (añoCobro, mesCobro) como haberes por-venta.
 *   - período de PAGO = mes de cobro + 1 (rolla el año en diciembre).
 *   - tipo 'comision', origen 'motor_comision', referencia = venta_id, monto = comisión.
 *   - CORTE: solo cobros con fecha_cobro_real >= COMISION_CORTE.
 *   - IDEMPOTENTE: clave (empleado_id, referencia venta_id). Si ya existe → skip. Correr 2× = igual.
 *
 * IO inyectable para test (sin tocar la DB): `obtenerDetalle`, `cargarReferencias`, `insertar`.
 * Uso real: pasar los deps de cómputo (los mismos de detalleLiquidableDelMes) + empleadoPorUsuario;
 * la IO cae a la DB por defecto. NO tiene UI.
 *
 * @returns {{ insertados:[], saltados:[], fueraDeCorte:[] }}
 */
export const materializarComisionesCobradas = async (añoCobro, mesCobro, deps = {}) => {
  const corte = deps.corte || COMISION_CORTE;
  const pagoMes = mesCobro === 12 ? 1 : mesCobro + 1;
  const pagoAño = mesCobro === 12 ? añoCobro + 1 : añoCobro;
  const cobroLabel = `${añoCobro}-${String(mesCobro).padStart(2, "0")}`;

  const empleadoPorUsuario = deps.empleadoPorUsuario || {};
  const usuarios = deps.usuarios || Object.keys(empleadoPorUsuario);
  const obtenerDetalle = deps.obtenerDetalle || ((u) => detalleLiquidableDelMes(u, añoCobro, mesCobro, deps));
  const cargarReferencias = deps.cargarReferencias || cargarReferenciasComisionDB;
  const insertar = deps.insertar || crearHaberEmpleado;

  const yaMaterializadas = await cargarReferencias();
  const res = { insertados: [], saltados: [], fueraDeCorte: [] };

  for (const usuarioId of usuarios) {
    const detalle = obtenerDetalle(usuarioId) || [];
    for (const d of detalle) {
      if (!d.empleado_id) continue; // sin puente usuario→empleado → no hay dónde materializar
      if (String(d.fecha_cobro_real) < corte) {
        res.fueraDeCorte.push({ usuario: String(usuarioId), venta: d.venta_id });
        continue;
      }
      const clave = `${d.empleado_id}:${d.venta_id}`;
      if (yaMaterializadas.has(clave)) {
        res.saltados.push({ usuario: String(usuarioId), venta: d.venta_id });
        continue;
      }
      const row = {
        empleado_id: d.empleado_id,
        periodo_año: pagoAño,
        periodo_mes: pagoMes,
        tipo: "comision",
        monto: d.comision,
        imponible: true,
        concepto: `Comisión venta ${d.venta_id} · ${d.cliente ?? "s/cliente"} · cobro ${cobroLabel}`,
        origen: "motor_comision",
        referencia: String(d.venta_id),
      };
      // eslint-disable-next-line no-await-in-loop
      await insertar(row);
      yaMaterializadas.add(clave); // idempotencia intra-run (misma venta repetida en el detalle)
      res.insertados.push({ usuario: String(usuarioId), venta: d.venta_id, row });
    }
  }

  return res;
};
